import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  HelpCircle, 
  ChevronDown, 
  Mail, 
  MessageCircle, 
  FileText, 
  Settings,
  Zap,
  Users,
  Shield
} from 'lucide-react';
import { AuthGuard } from '@/components/common/AuthGuard';
import { toast } from 'sonner';

const faqs = [
  {
    category: 'Getting Started',
    icon: Zap,
    questions: [
      {
        q: 'How do I connect my social media accounts?',
        a: 'Go to the Platforms page and click "Connect" for the platform you want to add. You\'ll be redirected to authenticate with that platform and grant permissions.'
      },
      {
        q: 'What social platforms are supported?',
        a: 'We currently support Twitter/X, Facebook, Instagram, TikTok, Reddit, and Threads. More platforms are being added regularly.'
      },
      {
        q: 'How do I set up content filtering?',
        a: 'Navigate to Content Filtering in your dashboard. You can create custom rules based on keywords, sentiment, authors, and more using our AI-powered filtering system.'
      }
    ]
  },
  {
    category: 'Account & Billing',
    icon: Users,
    questions: [
      {
        q: 'How does the credit system work?',
        a: 'Credits are used for AI operations like content analysis and filtering. Free accounts get 100 credits monthly, Pro gets 1,000, and Premium gets unlimited.'
      },
      {
        q: 'Can I upgrade or downgrade my plan?',
        a: 'Yes, you can change your plan at any time from the Upgrade page. Changes take effect immediately and billing is prorated.'
      },
      {
        q: 'How do I cancel my subscription?',
        a: 'You can manage your subscription through the customer portal accessible from your account settings.'
      }
    ]
  },
  {
    category: 'Features',
    icon: Settings,
    questions: [
      {
        q: 'What is AI content analysis?',
        a: 'Our AI analyzes your social media content for sentiment, toxicity, spam detection, and more to help you maintain brand safety and engagement quality.'
      },
      {
        q: 'How does social listening work?',
        a: 'Social listening monitors mentions of your brand, keywords, or topics across platforms in real-time, helping you stay on top of conversations about your business.'
      },
      {
        q: 'Can I customize the dashboard layout?',
        a: 'Yes, we offer multiple dashboard layouts (Overview, Detailed, Minimal) that you can switch between based on your preferences.'
      }
    ]
  },
  {
    category: 'Privacy & Security',
    icon: Shield,
    questions: [
      {
        q: 'How is my data protected?',
        a: 'We use enterprise-grade security measures including encryption at rest and in transit. Your social media credentials are stored securely using OAuth tokens.'
      },
      {
        q: 'Can I delete my account and data?',
        a: 'Yes, you can request account deletion from the Settings page. This will permanently remove all your data from our systems.'
      },
      {
        q: 'Do you sell my data?',
        a: 'No, we never sell your personal data or social media content. Your data is used solely to provide our services to you.'
      }
    ]
  }
];

export default function Help() {
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // TODO: Implement actual contact form submission
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast.success('Message sent successfully! We\'ll get back to you within 24 hours.');
      setContactForm({ subject: '', message: '', email: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthGuard>
      <div className="container mx-auto py-8 space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold">Help & Support</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Find answers to common questions or get in touch with our support team
          </p>
        </div>

        <Tabs defaultValue="faq" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="contact">Contact Support</TabsTrigger>
          </TabsList>

          <TabsContent value="faq" className="space-y-6">
            <div className="grid gap-6">
              {faqs.map((category) => {
                const Icon = category.icon;
                return (
                  <Card key={category.category}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Icon className="h-5 w-5" />
                        {category.category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {category.questions.map((faq, index) => (
                        <Collapsible key={index}>
                          <CollapsibleTrigger className="flex w-full items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted text-left">
                            <span className="font-medium">{faq.q}</span>
                            <ChevronDown className="h-4 w-4 transition-transform" />
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-2 p-4 bg-background border rounded-lg">
                            <p className="text-muted-foreground">{faq.a}</p>
                          </CollapsibleContent>
                        </Collapsible>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="contact" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Contact Form
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Send us a message and we'll respond within 24 hours
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Email</label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Subject</label>
                      <Input
                        placeholder="How can we help you?"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Message</label>
                      <Textarea
                        placeholder="Describe your question or issue..."
                        rows={5}
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageCircle className="h-5 w-5" />
                      Quick Help
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-medium text-blue-900">Getting Started</h3>
                      <p className="text-sm text-blue-700 mt-1">
                        New to the platform? Check out our onboarding guide to get up and running quickly.
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h3 className="font-medium text-green-900">Status Page</h3>
                      <p className="text-sm text-green-700 mt-1">
                        Check our status page for any ongoing issues or maintenance updates.
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h3 className="font-medium text-purple-900">Feature Requests</h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Have an idea for a new feature? We'd love to hear from you!
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Resources
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button variant="ghost" className="w-full justify-start">
                      📖 User Guide
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      🎥 Video Tutorials
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      🔧 API Documentation
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      💬 Community Forum
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AuthGuard>
  );
}